import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { 
  Building2, 
  Users, 
  CalendarCheck, 
  TrendingUp,
  ArrowRight,
  CheckCircle,
  XCircle,
  Clock,
  Mail,
  Receipt,
  Map,
  BarChart3,
  Target,
  Eye,
  MessageSquarePlus,
  Bug,
  Heart,
  AlertTriangle
} from "lucide-react";
import { PlanBadge } from "@/components/PlanBadge";
import { PiktableLoader } from "@/components/PiktableLoader";

interface GlobalStats {
  totalEstablishments: number;
  activeEstablishments: number;
  trialEstablishments: number;
  totalReservations: number;
  arrivedReservations: number;
  cancelledReservations: number;
  totalUsers: number;
  adminUsers: number;
  staffUsers: number;
  superAdminUsers: number;
  newLeadsThisWeek: number;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<GlobalStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [recentEstablishments, setRecentEstablishments] = useState<any[]>([]);

  useEffect(() => {
    fetchGlobalStats();
    fetchRecentEstablishments();
  }, []);

  const fetchGlobalStats = async () => {
    try {
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

      // Run all counts server-side in parallel — no 1000-row cap
      const [
        totalEst,
        activeEst,
        trialEst,
        totalRes,
        arrivedRes,
        cancelledRes,
        totalRoles,
        adminRoles,
        staffRoles,
        superAdmins,
        recentLeads,
      ] = await Promise.all([
        supabase.from('establishments').select('*', { count: 'exact', head: true }),
        supabase.from('establishments').select('*', { count: 'exact', head: true }).eq('is_active', true),
        supabase.from('establishments').select('*', { count: 'exact', head: true }).eq('plan_type', 'trial'),
        supabase.from('reservations').select('*', { count: 'exact', head: true }),
        supabase.from('reservations').select('*', { count: 'exact', head: true }).eq('status', 'arrivé'),
        supabase.from('reservations').select('*', { count: 'exact', head: true }).eq('status', 'annulé'),
        supabase.from('user_roles').select('*', { count: 'exact', head: true }),
        supabase.from('user_roles').select('*', { count: 'exact', head: true }).eq('role', 'admin'),
        supabase.from('user_roles').select('*', { count: 'exact', head: true }).eq('role', 'staff'),
        supabase.from('super_admins').select('*', { count: 'exact', head: true }),
        supabase.from('leads').select('*', { count: 'exact', head: true }).gte('created_at', oneWeekAgo.toISOString()),
      ]);

      setStats({
        totalEstablishments: totalEst.count || 0,
        activeEstablishments: activeEst.count || 0,
        trialEstablishments: trialEst.count || 0,
        totalReservations: totalRes.count || 0,
        arrivedReservations: arrivedRes.count || 0,
        cancelledReservations: cancelledRes.count || 0,
        totalUsers: (totalRoles.count || 0) + (superAdmins.count || 0),
        adminUsers: adminRoles.count || 0,
        staffUsers: staffRoles.count || 0,
        superAdminUsers: superAdmins.count || 0,
        newLeadsThisWeek: recentLeads.count || 0,
      });
    } catch (error) {
      console.error('Error fetching global stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentEstablishments = async () => {
    try {
      const { data, error } = await supabase
        .from('establishments')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      setRecentEstablishments(data || []);
    } catch (error) {
      console.error('Error fetching recent establishments:', error);
    }
  };

  if (loading) {
    return <PiktableLoader message="Chargement de l'administration..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
      <div className="container mx-auto p-6 max-w-7xl space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-serif font-bold flex items-center gap-3">
              <Building2 className="w-8 h-8 text-primary" />
              Administration globale
            </h1>
            <p className="text-muted-foreground mt-1">
              Vue d'ensemble de tous les établissements
            </p>
          </div>
          <Link to="/dashboard">
            <Button variant="outline">
              Retour au dashboard
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Établissements
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">
                {stats?.totalEstablishments}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {stats?.activeEstablishments} actifs
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                En période d'essai
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-amber-600">
                {stats?.trialEstablishments}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                établissements
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-indigo-500/10 to-indigo-600/5 border-indigo-500/20">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Utilisateurs
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-indigo-600">
                {stats?.totalUsers}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {stats?.adminUsers} admins, {stats?.staffUsers} staff{stats?.superAdminUsers ? `, ${stats.superAdminUsers} super` : ''}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <CalendarCheck className="w-4 h-4" />
                Réservations totales
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">
                {stats?.totalReservations}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {stats?.arrivedReservations} venues · {stats?.cancelledReservations} annulées
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Taux de venue
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-600">
                {stats?.totalReservations 
                  ? Math.round((stats.arrivedReservations / stats.totalReservations) * 100)
                  : 0}%
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                moyenne globale
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-pink-500/10 to-pink-600/5 border-pink-500/20">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Target className="w-4 h-4" />
                Prospects
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-pink-600">
                {stats?.newLeadsThisWeek}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                cette semaine
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Recent Establishments */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Derniers établissements</span>
                <Link to="/admin/establishments">
                  <Button variant="ghost" size="sm">
                    Voir tout
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentEstablishments.map((est) => (
                  <div
                    key={est.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1">
                      <p className="font-medium">{est.name}</p>
                      <p className="text-sm text-muted-foreground">/{est.slug}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {est.is_active ? (
                        <Badge className="bg-green-500/10 text-green-600">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Actif
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          <XCircle className="w-3 h-3 mr-1" />
                          Inactif
                        </Badge>
                      )}
                      <PlanBadge 
                        planType={est.plan_type} 
                        trialEndsAt={est.trial_ends_at}
                        showTooltip={false}
                        size="sm"
                      />
                    </div>
                  </div>
                ))}
                {recentEstablishments.length === 0 && (
                  <p className="text-center text-muted-foreground py-4">
                    Aucun établissement
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Quick Links */}
          <Card>
            <CardHeader>
              <CardTitle>Actions rapides</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link to="/admin/establishments" className="block">
                <Button variant="outline" className="w-full justify-start h-auto py-4">
                  <Building2 className="w-5 h-5 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">Gérer les établissements</p>
                    <p className="text-sm text-muted-foreground">
                      Activer, désactiver, modifier
                    </p>
                  </div>
                </Button>
              </Link>
              <Link to="/admin/users" className="block">
                <Button variant="outline" className="w-full justify-start h-auto py-4">
                  <Users className="w-5 h-5 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">Gérer les utilisateurs</p>
                    <p className="text-sm text-muted-foreground">
                      Voir tous les utilisateurs de tous les établissements
                    </p>
                  </div>
                </Button>
              </Link>
              <Link to="/admin/email-templates" className="block">
                <Button variant="outline" className="w-full justify-start h-auto py-4">
                  <Mail className="w-5 h-5 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">Emails système</p>
                    <p className="text-sm text-muted-foreground">
                      Personnaliser les emails de vérification et d'invitation
                    </p>
                  </div>
                </Button>
              </Link>
              <Link to="/admin/billing" className="block">
                <Button variant="outline" className="w-full justify-start h-auto py-4">
                  <Receipt className="w-5 h-5 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">Facturation</p>
                    <p className="text-sm text-muted-foreground">
                      Gérer la facturation de tous les établissements
                    </p>
                  </div>
                </Button>
              </Link>
              <Link to="/admin/floor-plan-requests" className="block">
                <Button variant="outline" className="w-full justify-start h-auto py-4">
                  <Map className="w-5 h-5 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">Demandes plan de salle</p>
                    <p className="text-sm text-muted-foreground">
                      Gérer les demandes d'assistance de configuration
                    </p>
                  </div>
                </Button>
              </Link>
              <Link to="/admin/emails" className="block">
                <Button variant="outline" className="w-full justify-start h-auto py-4">
                  <BarChart3 className="w-5 h-5 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">Statistiques emails</p>
                    <p className="text-sm text-muted-foreground">
                      Voir tous les emails automatiques envoyés
                    </p>
                  </div>
                </Button>
              </Link>
              <div className="border-t pt-3 mt-3">
                <p className="text-xs text-muted-foreground mb-3 uppercase tracking-wider">Gestion des prospects</p>
                <Link to="/admin/client-followup" className="block mb-3">
                  <Button variant="outline" className="w-full justify-start h-auto py-4 border-primary/30 hover:bg-primary/5">
                    <Heart className="w-5 h-5 mr-3 text-primary" />
                    <div className="text-left">
                      <p className="font-medium">Suivi clients</p>
                      <p className="text-sm text-muted-foreground">
                        Suivi commercial : visites terrain, notes, contacts, santé client
                      </p>
                    </div>
                  </Button>
                </Link>
                <Link to="/admin/leads" className="block">
                  <Button variant="outline" className="w-full justify-start h-auto py-4 border-pink-500/30 hover:bg-pink-500/5">
                    <Target className="w-5 h-5 mr-3 text-pink-500" />
                    <div className="text-left">
                      <p className="font-medium">Gestion des prospects</p>
                      <p className="text-sm text-muted-foreground">
                        Suivi des leads entrants (web, Facebook, etc.)
                      </p>
                    </div>
                  </Button>
                </Link>
              </div>
              <div className="border-t pt-3 mt-3">
                <p className="text-xs text-muted-foreground mb-3 uppercase tracking-wider">Marketing</p>
                <Link to="/admin/marketing?project=piktable" className="block">
                  <Button variant="outline" className="w-full justify-start h-auto py-4 border-pink-500/30 hover:bg-pink-500/5">
                    <svg className="w-5 h-5 mr-3 text-pink-500" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                    <div className="text-left">
                      <p className="font-medium">Instagram automatisé</p>
                      <p className="text-sm text-muted-foreground">
                        Pipeline complet : brand context, templates multi-formats, planification, analytics
                      </p>
                    </div>
                  </Button>
                </Link>
              </div>
              <div className="border-t pt-3 mt-3">
                <p className="text-xs text-muted-foreground mb-3 uppercase tracking-wider">Support utilisateurs</p>
                <Link to="/admin/feedbacks" className="block">
                  <Button variant="outline" className="w-full justify-start h-auto py-4 border-teal-500/30 hover:bg-teal-500/5">
                    <MessageSquarePlus className="w-5 h-5 mr-3 text-teal-500" />
                    <div className="text-left">
                      <p className="font-medium">Feedbacks utilisateurs</p>
                      <p className="text-sm text-muted-foreground">
                        Bugs, suggestions et questions des utilisateurs
                      </p>
                    </div>
                  </Button>
                </Link>
              </div>
              <div className="border-t pt-3 mt-3">
                <p className="text-xs text-muted-foreground mb-3 uppercase tracking-wider">Outils de développement</p>
                <Link to="/admin/errors" className="block">
                  <Button variant="outline" className="w-full justify-start h-auto py-4 border-red-500/30 hover:bg-red-500/5">
                    <Bug className="w-5 h-5 mr-3 text-red-500" />
                    <div className="text-left">
                      <p className="font-medium">Erreurs Sentry</p>
                      <p className="text-sm text-muted-foreground">
                        Voir les erreurs capturées en production
                      </p>
                    </div>
                  </Button>
                </Link>
                <Link to="/admin/manager-notifications" className="block mt-3">
                  <Button variant="outline" className="w-full justify-start h-auto py-4 border-amber-500/30 hover:bg-amber-500/5">
                    <AlertTriangle className="w-5 h-5 mr-3 text-amber-500" />
                    <div className="text-left">
                      <p className="font-medium">Notifications managers</p>
                      <p className="text-sm text-muted-foreground">
                        Audit collisions & popups par établissement
                      </p>
                    </div>
                  </Button>
                </Link>
                <Link to="/admin/onboarding-preview" className="block mt-3">
                  <Button variant="outline" className="w-full justify-start h-auto py-4 border-dashed">
                    <Eye className="w-5 h-5 mr-3" />
                    <div className="text-left">
                      <p className="font-medium">Aperçu onboarding</p>
                      <p className="text-sm text-muted-foreground">
                        Visualiser le parcours d'inscription (mode démo)
                      </p>
                    </div>
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
